/**
 * "Reopen the chat on the next page": when the visitor follows a same-tab link from the chat (the `link` button,
 * "Vedi" on a product card) the panel is opened again on the page they land on — on desktop only; on a phone the panel
 * covers the whole screen and would hide the page they asked to see.
 *
 * The mark lives in `sessionStorage` (this tab only), is consumed by the first page load that follows and expires
 * after a minute, so a later, unrelated page load never reopens the panel.
 */
const PREFIX = 'aicommerce:reopen:';
export const REOPEN_TTL_MS = 60_000;

interface ClickLike {
  button?: number;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  defaultPrevented?: boolean;
}

/** A plain primary click on a link to this origin navigates this tab (modifier keys open a new tab or window). */
export function navigatesThisTab(e: ClickLike, href: string): boolean {
  if (e.defaultPrevented || (e.button ?? 0) !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return false;
  try {
    return new URL(href, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

export function markReopen(tenantKey: string, now = Date.now()): void {
  try {
    window.sessionStorage.setItem(PREFIX + tenantKey, String(now));
  } catch {
    /* storage blocked: the panel just stays closed on the next page */
  }
}

export function clearReopen(tenantKey: string): void {
  try {
    window.sessionStorage.removeItem(PREFIX + tenantKey);
  } catch {
    /* ignore */
  }
}

/** Reads and removes the mark; true when it was set less than `REOPEN_TTL_MS` ago. */
export function takeReopen(tenantKey: string, now = Date.now()): boolean {
  try {
    const raw = window.sessionStorage.getItem(PREFIX + tenantKey);
    if (raw === null) return false;
    window.sessionStorage.removeItem(PREFIX + tenantKey);
    const at = Number(raw);
    return Number.isFinite(at) && now - at >= 0 && now - at < REOPEN_TTL_MS;
  } catch {
    return false;
  }
}

/** Same breakpoint as the panel CSS: up to 640 px the panel is full screen (phone). */
export function isDesktopViewport(): boolean {
  try {
    return window.matchMedia('(min-width: 641px)').matches;
  } catch {
    return false;
  }
}
